import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { lazy, Suspense } from 'react';
import { AuthProvider, useAuth } from './hooks/useAuth';
import { ProtectedRoute } from './components/ProtectedRoute';
import Layout from './components/Layout';
import { ToastProvider } from './contexts/ToastContext';
import ToastContainer from './components/notifications/ToastContainer';
import { TutorialProvider } from './contexts/TutorialContext';
import ErrorBoundary from './components/ErrorBoundary';

// Common pages - these are small, load normally
import Dashboard from './pages/Dashboard';
import PublicDashboard from './pages/PublicDashboard';

// Lazy load heavy pages for code splitting
const Login = lazy(() => import('./pages/Login'));
const OAuthCallback = lazy(() => import('./pages/OAuthCallback'));
const CompleteProfile = lazy(() => import('./pages/CompleteProfile'));
const NotificationPreferences = lazy(() => import('./pages/NotificationPreferences'));

// Admin pages
const AdminDashboard = lazy(() => import('./pages/admin/Dashboard'));
const UserManagement = lazy(() => import('./pages/admin/UserManagement'));
const SemesterManagement = lazy(() => import('./pages/admin/SemesterManagement'));
const CourseManagement = lazy(() => import('./pages/admin/CourseManagement'));
const SystemSettings = lazy(() => import('./pages/admin/SystemSettings'));

// Lecturer pages
const LecturerDashboard = lazy(() => import('./pages/lecturer/Dashboard'));
const MilestoneManagement = lazy(() => import('./pages/lecturer/MilestoneManagement'));
const TopicManagement = lazy(() => import('./pages/lecturer/TopicManagement'));
const ReviewSubmissions = lazy(() => import('./pages/lecturer/ReviewSubmissions'));

// Student pages
const StudentDashboard = lazy(() => import('./pages/student/Dashboard'));
const MyGroup = lazy(() => import('./pages/student/MyGroup'));
const Milestones = lazy(() => import('./pages/student/Milestones'));
const GroupDiscussions = lazy(() => import('./pages/student/GroupDiscussions'));

// Loading skeleton component
function PageLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <div className="text-center">
        <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-blue-600 border-t-transparent mb-4"></div>
        <p className="text-slate-600 font-medium">Loading...</p>
      </div>
    </div>
  );
}

function PublicRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-gray-500">Loading...</div>
      </div>
    );
  }

  return user ? <Navigate to="/" /> : children;
}

function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <ToastProvider>
          <TutorialProvider>
            <AuthProvider>
              <Suspense fallback={<PageLoader />}>
                <ToastContainer />
                <Routes>
            {/* Public routes */}
            <Route path="/" element={<PublicDashboard />} />
            <Route path="/login" element={
              <PublicRoute><Login /></PublicRoute>
            } />
            <Route path="/auth/callback" element={<OAuthCallback />} />
            <Route path="/complete-profile" element={<CompleteProfile />} />

          {/* Protected routes with layout */}
          <Route path="/dashboard" element={
            <ProtectedRoute>
              <Layout />
            </ProtectedRoute>
          }>
            {/* Dashboard - redirects based on role */}
            <Route index element={<Dashboard />} />

            {/* Notification Preferences */}
            <Route path="notification-preferences" element={
              <ProtectedRoute>
                <NotificationPreferences />
              </ProtectedRoute>
            } />

            {/* Admin routes */}
            <Route path="admin">
              <Route path="dashboard" element={
                <ProtectedRoute requireRole="admin">
                  <AdminDashboard />
                </ProtectedRoute>
              } />
              <Route path="users" element={
                <ProtectedRoute requireRole="admin">
                  <UserManagement />
                </ProtectedRoute>
              } />
              <Route path="semesters" element={
                <ProtectedRoute requireRole="admin">
                  <SemesterManagement />
                </ProtectedRoute>
              } />
              <Route path="courses" element={
                <ProtectedRoute requireRole="admin">
                  <CourseManagement />
                </ProtectedRoute>
              } />
              <Route path="settings" element={
                <ProtectedRoute requireRole="admin">
                  <SystemSettings />
                </ProtectedRoute>
              } />
            </Route>

            {/* Lecturer routes */}
            <Route path="lecturer">
              <Route path="dashboard" element={
                <ProtectedRoute requireRole={['admin', 'lecturer']}>
                  <LecturerDashboard />
                </ProtectedRoute>
              } />
              <Route path="milestones" element={
                <ProtectedRoute requireRole={['admin', 'lecturer']}>
                  <MilestoneManagement />
                </ProtectedRoute>
              } />
              <Route path="topics" element={
                <ProtectedRoute requireRole={['admin', 'lecturer']}>
                  <TopicManagement />
                </ProtectedRoute>
              } />
              <Route path="submissions" element={
                <ProtectedRoute requireRole={['admin', 'lecturer']}>
                  <ReviewSubmissions />
                </ProtectedRoute>
              } />
            </Route>

            {/* Student routes */}
            <Route path="student">
              <Route path="dashboard" element={
                <ProtectedRoute requireRole="student">
                  <StudentDashboard />
                </ProtectedRoute>
              } />
              <Route path="group" element={
                <ProtectedRoute requireRole="student">
                  <MyGroup />
                </ProtectedRoute>
              } />
              <Route path="milestones" element={
                <ProtectedRoute requireRole="student">
                  <Milestones />
                </ProtectedRoute>
              } />
              <Route path="discussions" element={
                <ProtectedRoute requireRole="student">
                  <GroupDiscussions />
                </ProtectedRoute>
              } />
            </Route>
          </Route>
          </Routes>
          </Suspense>
        </AuthProvider>
        </TutorialProvider>
      </ToastProvider>
    </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;
